import { useMemo, useState } from "react";
import { Button, ButtonGroup } from "@datapay-by-datacom/design-system";
import {
  useAgentInvocationPoll,
  useInvokeAgent,
  isTerminalInvocationStatus,
} from "../../../agent-catalog";
import type {
  AgentInvocationStatus,
  AgentRuntimeInput,
} from "../../../generated/core-api";
import { useOrgParams } from "../../hooks";
import { useOrgInvocableAgentTargets } from "../../hooks/OrgAgent/useOrgInvocableAgentTargets";
import { getServiceAccountLabel } from "../../utils/serviceAccountDisplay";
import {
  formatInvocationError,
  parseRuntimeResult,
  prettyPrintJson,
} from "../../utils/agentInvocationResult";
import styles from "./OrgAgent.module.css";

export function OrgAgentInvocationResult({
  invocation,
  isPolling,
}: {
  invocation: AgentInvocationStatus;
  isPolling: boolean;
}) {
  const [showRaw, setShowRaw] = useState(false);
  const parsedResult = parseRuntimeResult(invocation.result);
  const answer = parsedResult?.answer?.trim();
  const sources = parsedResult?.sources?.filter(Boolean) ?? [];
  const hasError =
    invocation.error !== null &&
    invocation.error !== undefined &&
    invocation.error !== "";

  return (
    <div className={styles.resultCard}>
      <div>
        Status:{" "}
        <span className={styles.statusBadge}>{invocation.status}</span>
      </div>
      {isPolling && <p className={styles.muted}>Polling for result…</p>}
      {hasError && (
        <pre className={styles.errorPre}>
          {formatInvocationError(invocation.error)}
        </pre>
      )}
      {answer && <div className={styles.resultAnswer}>{answer}</div>}
      {sources.length > 0 && (
        <div className={styles.sources}>
          <strong className={styles.sourcesTitle}>Sources</strong>
          <ul className={styles.sourcesList}>
            {sources.map((source) => (
              <li key={source}>{source}</li>
            ))}
          </ul>
        </div>
      )}
      <button
        type="button"
        className={styles.rawToggle}
        aria-expanded={showRaw}
        onClick={() => setShowRaw((open) => !open)}
      >
        {showRaw ? "Hide raw JSON" : "Show raw JSON"}
      </button>
      {showRaw && (
        <pre className={styles.rawJson}>{prettyPrintJson(invocation)}</pre>
      )}
    </div>
  );
}

export function OrgAgentInvocationPanel() {
  const { category, id: orgId } = useOrgParams();
  const { targets, isLoading, isError } = useOrgInvocableAgentTargets(
    orgId,
    category,
  );

  const [selectedAgentId, setSelectedAgentId] = useState("");
  const [selectedServiceAccountId, setSelectedServiceAccountId] = useState("");
  const [instruction, setInstruction] = useState("");
  const [workflowId, setWorkflowId] = useState<string | null>(null);

  const selectedTarget = useMemo(
    () => targets.find((target) => target.agent.id === selectedAgentId),
    [targets, selectedAgentId],
  );
  const orgAccounts = selectedTarget?.serviceAccounts ?? [];

  const invokeMutation = useInvokeAgent(selectedAgentId);
  const pollQuery = useAgentInvocationPoll(selectedAgentId, workflowId);

  const handleSend = () => {
    if (!selectedAgentId || !selectedServiceAccountId || !instruction.trim()) {
      return;
    }
    const inputPayload: AgentRuntimeInput = {
      workflow: "agent_runtime",
      instruction: instruction.trim(),
    };
    invokeMutation.mutate(
      {
        service_account_id: selectedServiceAccountId,
        input_payload: inputPayload,
      },
      {
        onSuccess: (response) => {
          setWorkflowId(response.workflow_id);
        },
      },
    );
  };

  const isPolling =
    !!workflowId &&
    !!pollQuery.data &&
    !isTerminalInvocationStatus(pollQuery.data.status);

  const hasInvocableTargets = targets.length > 0;

  return (
    <section className={styles.section}>
      <h2 className={styles.sectionTitle}>Test agent</h2>
      <p className={styles.sectionDescription}>
        Send a natural-language instruction using the agent runtime workflow.
        Responses are polled until the invocation completes.
      </p>

      {isLoading && <p className={styles.muted}>Loading agents…</p>}
      {isError && (
        <p className={styles.errorText}>Failed to load agents for this organisation.</p>
      )}
      {!isLoading && !isError && !hasInvocableTargets && (
        <p className={styles.muted}>
          No agents are linked to this organisation yet. A provisioner can attach
          an agent with a service account from the section above.
        </p>
      )}

      {hasInvocableTargets && (
        <>
          <div className={styles.fieldRow}>
            <label htmlFor="invoke-agent">Agent</label>
            <select
              id="invoke-agent"
              className={styles.select}
              value={selectedAgentId}
              onChange={(e) => {
                setSelectedAgentId(e.target.value);
                setSelectedServiceAccountId("");
              }}
            >
              <option value="">Select agent…</option>
              {targets.map(({ agent }) => (
                <option key={agent.id} value={agent.id}>
                  {agent.blueprint.name}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.fieldRow}>
            <label htmlFor="invoke-sa">Service account</label>
            <select
              id="invoke-sa"
              className={styles.select}
              value={selectedServiceAccountId}
              onChange={(e) => setSelectedServiceAccountId(e.target.value)}
              disabled={!selectedAgentId}
            >
              <option value="">Select service account…</option>
              {orgAccounts.map((account) => (
                <option key={account.id} value={account.id}>
                  {getServiceAccountLabel(account)}
                </option>
              ))}
            </select>
          </div>
        </>
      )}

      <div className={styles.fieldRow}>
        <label htmlFor="invoke-instruction">Instruction</label>
        <textarea
          id="invoke-instruction"
          className={styles.textArea}
          value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
          placeholder="Ask the agent a question…"
          disabled={!hasInvocableTargets}
        />
      </div>

      <ButtonGroup>
        <Button
          onPress={handleSend}
          isDisabled={
            invokeMutation.isPending ||
            isPolling ||
            !selectedAgentId ||
            !selectedServiceAccountId ||
            !instruction.trim()
          }
        >
          {invokeMutation.isPending ? "Sending…" : "Send"}
        </Button>
      </ButtonGroup>

      {invokeMutation.isError && (
        <p className={styles.errorText}>Invocation failed to start.</p>
      )}

      {workflowId && pollQuery.data && (
        <OrgAgentInvocationResult
          invocation={pollQuery.data}
          isPolling={!!isPolling}
        />
      )}
    </section>
  );
}
